import { z } from 'zod';
import { AppointmentSource, AppointmentStatus } from '../enums.js';
import { IsoDateTime, Pence, Uuid } from '../primitives.js';

/**
 * A diary booking. Carries `salonId` + nullable `renterId` (mirrors the owning staff's book).
 * Owners/managers see every appointment (floor management); renters see only their own.
 * The full client *record* behind a sealed appointment stays sealed (clients RLS), so a
 * sealed booking exposes only operational fields, not the client's contact/history.
 */
export const Appointment = z
  .object({
    id: Uuid,
    salonId: Uuid,
    renterId: Uuid.nullable(),
    locationId: Uuid.nullable(),
    staffId: Uuid,
    clientId: Uuid.nullable().describe('null for anonymous walk-ins'),
    serviceId: Uuid,
    clientDisplayName: z.string().max(160).describe('denormalised for floor visibility'),
    startAt: IsoDateTime,
    endAt: IsoDateTime,
    status: AppointmentStatus,
    source: AppointmentSource,
    priceQuoted: Pence,
    depositPaid: Pence,
    notes: z.string().max(2000).nullable(),
    queueTicketId: Uuid.nullable().describe('set when this booking was filled from the queue'),
    seriesId: Uuid.nullable().describe(
      'set when this booking is an occurrence of a recurring series',
    ),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
  })
  .meta({ id: 'Appointment' });
export type Appointment = z.infer<typeof Appointment>;

export const CreateAppointment = z
  .object({
    staffId: Uuid,
    serviceId: Uuid,
    /**
     * Optional pricing variant of the service (short/medium/long). When set it drives the CONCRETE
     * priceQuoted (resolved server-side via the pricing engine, frozen at booking so shown == charged)
     * AND the chair time (the variant's own duration + the service's processing time → endAt). When
     * omitted, behaviour is unchanged: the appointment records the service's derived "from £X" price.
     */
    variantId: Uuid.nullish(),
    clientId: Uuid.nullish(),
    clientDisplayName: z.string().max(160).optional(),
    startAt: IsoDateTime,
    /** Optional: override the service's computed end. Otherwise derived from the service. */
    endAt: IsoDateTime.nullish(),
    source: AppointmentSource.default('salon'),
    notes: z.string().max(2000).nullish(),
    depositPaid: Pence.default(0),
    /** Idempotency key for safe retries from flaky salon Wi-Fi. */
    idempotencyKey: z.string().min(8).max(200).optional(),
  })
  .meta({ id: 'CreateAppointment' });
export type CreateAppointment = z.infer<typeof CreateAppointment>;

export const UpdateAppointment = z
  .object({
    staffId: Uuid.optional(),
    serviceId: Uuid.optional(),
    startAt: IsoDateTime.optional(),
    endAt: IsoDateTime.nullish(),
    status: AppointmentStatus.optional(),
    notes: z.string().max(2000).nullish(),
    clientDisplayName: z.string().max(160).optional(),
  })
  .meta({ id: 'UpdateAppointment' });
export type UpdateAppointment = z.infer<typeof UpdateAppointment>;

export const ListAppointmentsQuery = z.object({
  from: IsoDateTime.describe('inclusive window start'),
  to: IsoDateTime.describe('exclusive window end'),
  staffId: Uuid.optional(),
});
export type ListAppointmentsQuery = z.infer<typeof ListAppointmentsQuery>;
